'use client';

/**
 * AuthProvider + useAuth — Context Provider pattern (P-282 fix).
 *
 * SPEC-167 (Sprint 83, 2026-09-10): refactor useAuth from per-instance
 * hook (32 call sites → 32 useEffect + 32 api.me() calls per page load,
 * 190+ requests per session) to a shared Context Provider.
 *
 * Strategy (backward-compatible):
 * - AuthProvider mounts ONCE at root layout.
 * - It runs the auth bootstrap (tryRefresh + api.me) ONCE per app session.
 * - useAuth() now reads from context — no per-instance fetches.
 * - The public API of useAuth() is UNCHANGED: { user, loading, logout, refresh }.
 *   All 32 call sites continue to work without modification.
 * - notifyUserContextChanged() dispatches `profileia:user-changed` event,
 *   which the AuthProvider listens to and triggers a single refetch.
 *
 * §6 Anti-Invención: 0 business logic changes. Pure infrastructure
 * refactor — the auth flow (refresh → api.me → setUser) is identical to
 * the previous per-instance implementation.
 *
 * Spec ref: SPEC-167 (P-282), CONTRATO-IA-PROGRAMADORA §2.1 (Spec-First).
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useRouter } from 'next/navigation';
import { api, tokenStore, tryRefresh, ApiClientError } from './api-client';
import { useTokenRefresh } from '@/hooks/useTokenRefresh';
import { unregisterServiceWorker } from './sw-register';
import type { PublicUser } from '@profileia/shared';

// ─── Context ─────────────────────────────────────────────────────────────

interface AuthContextValue {
  user: PublicUser | null;
  loading: boolean;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// ─── Provider ────────────────────────────────────────────────────────────

export function AuthProvider({ children }: { children: ReactNode }) {
  // ADR-070 §1: refresh proactivo — el access token nunca expira en runtime.
  // Montado UNA vez aquí (root layout, patrón P-282): cada 30s chequea el TTL
  // y a <60s de expirar hace POST /api/auth/refresh (cookie httpOnly) — el
  // socket nunca se desconecta por JWT expiry.
  useTokenRefresh();

  const [user, setUser] = useState<PublicUser | null>(null);
  const [loading, setLoading] = useState(true);
  const router = useRouter();

  // AUDIT3-SHARED-020: AbortController for canceling in-flight `api.me()`
  // calls. Captured in a ref so the cleanup function returned by the
  // mount effect can abort() without stale-closure issues. Reused across
  // `refresh()` invocations (each call aborts the previous one).
  const abortRef = useRef<AbortController | null>(null);

  const refresh = useCallback(async () => {
    // Abort any in-flight refresh from a previous `refresh()` invocation.
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    const access = tokenStore.getAccess();
    if (access) {
      // Existing path: fetch user with existing token
      try {
        const me = await api.me(controller.signal);
        if (controller.signal.aborted) return;
        setUser(me);
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        if (err instanceof ApiClientError && err.status === 401) {
          tokenStore.clear();
          setUser(null);
          // P-109: set global flag so AppShell / admin layout can redirect
          // to the appropriate login page with ?reason=session_expired.
          if (typeof window !== 'undefined') {
            if (!window.__profileia) window.__profileia = { access: null };
            window.__profileia.sessionExpired = true;
            window.dispatchEvent(new CustomEvent('profileia:session-expired'));
          }
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    } else {
      // C-06 fix: After reload, access token is null (in-memory only).
      // Try silent refresh via httpOnly cookie before giving up.
      try {
        const refreshed = await tryRefresh();
        if (controller.signal.aborted) return;
        if (refreshed) {
          const me = await api.me(controller.signal);
          if (controller.signal.aborted) return;
          setUser(me);
        } else {
          setUser(null);
        }
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        setUser(null);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }
  }, []);

  // Bootstrap: run ONCE on provider mount. All useAuth() consumers share
  // this single fetch (was 32 fetches per page load pre-refactor).
  useEffect(() => {
    // FIX 2026-09-20 (Founder report: "401 en consola del browser"):
    // Pre-fix: el AuthProvider SIEMPRE llamaba `refresh()` al montar.
    // En páginas públicas (guests en /sala/{slug}, /live/{slug}, /login,
    // /register), esto hacía un POST /api/auth/refresh que fallaba con
    // 401 (no hay refresh cookie para guests) → aparecía como error rojo
    // en consola del browser.
    // Post-fix: si la ruta actual es pública (no requiere auth), skip
    // el tryRefresh — solo hacemos refresh si hay un access token en
    // memoria (usuario que estaba logueado pero el token expiró).
    // Anti-§6: no business logic, solo optimización de red + UX (no
    // errores 401 cosméticos en consola).
    if (typeof window !== 'undefined') {
      const path = window.location.pathname;
      // Issue #9 fix (2026-10-05, sesión v102): /sala/ YA NO salta el
      // tryRefresh. Un usuario logueado que abre un enlace de sala en
      // pestaña nueva no tiene access token (sessionStorage es per-tab,
      // MTRX-001) pero SÍ tiene refresh cookie httpOnly válida — el skip
      // lo trataba como guest SIEMPRE. tryRefresh() es SILENCIOSO en 401
      // (api-client.ts: devuelve false, sin console.error) → NO se
      // reintroducen los 401 cosméticos del FIX 2026-09-20. El resto de
      // rutas públicas (/live/, /login, /register, /forgot-password,
      // /reset-password) mantienen el skip — Issue #9 es solo salas.
      const isPublicRoute = path.startsWith('/live/') ||
                           path === '/login' ||
                           path === '/register' ||
                           path === '/forgot-password' ||
                           path === '/reset-password';
      const hasAccessToken = !!tokenStore.getAccess();
      if (isPublicRoute && !hasAccessToken) {
        // Guest en ruta pública — no intentar refresh, no hay sesión.
        setUser(null);
        setLoading(false);
        return;
      }
    }
    // P-370 fix: add .catch() to the fire-and-forget refresh() call —
    // without it, any rejection that escapes the try/catch inside
    // refresh() surfaces as "Uncaught (in promise) undefined" on the
    // console. The refresh() function has comprehensive try/catch blocks,
    // but .catch() is defensive programming against future code changes.
    void refresh().catch(() => {
      // Best-effort — the user will see the redirect to /login if auth fails
    });
    return () => {
      abortRef.current?.abort();
      abortRef.current = null;
    };
  }, [refresh]);

  // P-188: subscribe to `profileia:user-changed` — single listener at the
  // provider level (was 32 listeners pre-refactor, each triggering a
  // separate api.me() call).
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handler = () => {
      // P-370 fix: add .catch() — defensive against unhandled rejections
      void refresh().catch(() => {});
    };
    window.addEventListener('profileia:user-changed', handler);
    return () => window.removeEventListener('profileia:user-changed', handler);
  }, [refresh]);

  const logout = useCallback(async () => {
    // 1. Notify backend to revoke the refresh token + clear httpOnly cookie.
    // SPEC-240 v2 (Founder #44): also call POST /api/admin/logout to clear
    // the profileia_admin_auth cookie (in case the user was logged in as
    // AdminAccount via PATH B). Best-effort — failures don't block local
    // cleanup.
    try {
      await api.logout();
    } catch {
      // Network failure must NOT block local cleanup.
    }
    try {
      await fetch('/api/admin/logout', {
        method: 'POST',
        credentials: 'include',
      });
    } catch {
      // Non-fatal — admin cookie will expire naturally (30min TTL).
    }

    // 2. Clear in-memory access token (defensive).
    tokenStore.clear();

    // 3. Clear stale sessionExpired flag.
    if (typeof window !== 'undefined') {
      if (!window.__profileia) window.__profileia = { access: null };
      window.__profileia.sessionExpired = false;
      window.dispatchEvent(new CustomEvent('profileia:logged-out'));
    }

    // 4. Unregister SW + clear SW-owned caches (avoid cross-user cache leak).
    // P-370 fix: add .catch() — defensive against unhandled rejections.
    void unregisterServiceWorker().catch(() => {});

    // 5. Reset user state — triggers re-render of all consumers reading
    // from this context (was: re-render of only the consumer that called
    // logout, leaving others stale).
    setUser(null);

    // 6. Redirect to login (use router.replace so user can't back into auth-gated page).
    router.replace('/login?reason=logout');
  }, [router]);

  const value: AuthContextValue = { user, loading, logout, refresh };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// ─── useAuth hook (backward-compatible with old API) ─────────────────────

/**
 * useAuth — read auth state from the AuthProvider context.
 *
 * The provider MUST be mounted at the root layout. If a consumer calls
 * `useAuth()` outside of an `<AuthProvider>` tree, this throws a clear
 * error pointing to the fix (mount `<AuthProvider>` in `app/layout.tsx`).
 */
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error(
      'useAuth() must be used inside an <AuthProvider>. Wrap your root ' +
        'layout with <AuthProvider> — see frontend/src/app/layout.tsx.',
    );
  }
  return ctx;
}

// ─── P-188 (SPEC-120 Task F) — cross-instance user context refresh ──────────

/**
 * Dispatch `profileia:user-changed` so the AuthProvider re-fetches
 * `/api/auth/me` ONCE and updates all consumers via context.
 *
 * Call this AFTER a mutation that changes server-side user data visible in
 * the app chrome — currently: `/packages/purchase/with-balance` success
 * (tier change) + `/market/credits/purchase` completed (credits + possible
 * free-package activation per INV-MKT-03).
 *
 * §6 Anti-Invención: 0 business logic — a UI invalidation signal only.
 * Fire-and-forget (no payload).
 */
export function notifyUserContextChanged(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent('profileia:user-changed'));
}
